import axios from 'axios';

/**
 * Pre-configured Axios instance for API calls.
 * Uses Sanctum stateful cookie auth for web clients.
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

function resolveApiOrigin() {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return window.location.origin;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const csrfClient = axios.create({
  baseURL: resolveApiOrigin(),
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
});

const PUBLIC_API_PREFIXES = [
  '/v1/menus/public/',
  '/v1/orders/lookup-by-session',
  '/v1/orders/resolve-success-token',
  '/v1/orders/pay-at-table/info',
  '/v1/orders/pay-at-table/checkout',
  '/v1/orders/status',
  '/v1/orders/confirmation',
  '/v1/orders/receipt',
  '/v1/orders/thermal',
  '/v1/customers/lookup',
  '/v1/waiter/call',
  '/v1/track/dish',
  '/v1/contact',
  '/v1/locales',
  '/v1/plans',
];

function isPublicApiRequest(url = '', method = 'get') {
  if (!url) return false;
  const normalizedMethod = String(method || 'get').toLowerCase();
  let normalized = url;
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    try {
      normalized = new URL(normalized).pathname;
    } catch {
      normalized = url;
    }
  }
  if (normalized.startsWith('/api/')) {
    normalized = normalized.slice('/api'.length);
  }
  if (PUBLIC_API_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;
  // Order status/receipt endpoints are public only for read requests.
  return ['get', 'head'].includes(normalizedMethod)
    && (
      /^\/v1\/orders\/\d+\/(status|confirmation|receipt|thermal)$/.test(normalized)
      || /^\/v1\/orders\/(status|confirmation|receipt|thermal)$/.test(normalized)
    );
}

function isPublicPagePath(pathname = '') {
  return pathname.startsWith('/m/') || pathname.startsWith('/order/success') || pathname.startsWith('/order/pay-at-table');
}

function isProtectedAppPath(pathname = '') {
  const protectedPrefixes = [
    '/dashboard',
    '/onboarding',
    '/analytics',
    '/orders',
    '/customers',
    '/menus',
    '/dishes',
    '/categories',
    '/option-groups',
    '/tables',
    '/locations',
    '/aade',
    '/subscription',
    '/printers',
    '/employees',
    '/duties',
    '/employee-dashboard',
    '/my-shift',
    '/kds',
    '/profile',
    '/company',
    '/settings',
    '/widget',
    '/media',
    '/admin',
  ];
  return protectedPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

let csrfCookieRequestPromise = null;

async function ensureCsrfCookie() {
  if (!csrfCookieRequestPromise) {
    csrfCookieRequestPromise = csrfClient
      .get('/api/v1/auth/csrf-cookie')
      .finally(() => {
        csrfCookieRequestPromise = null;
      });
  }
  return csrfCookieRequestPromise;
}

function shouldEnsureCsrfCookie(config) {
  const method = String(config?.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) {
    return false;
  }
  if (config?.skipCsrf === true) {
    return false;
  }
  const url = String(config?.url || '');
  return !url.includes('/sanctum/csrf-cookie');
}

function hasBearerAuthorization(config) {
  const authHeader = config?.headers?.Authorization || config?.headers?.authorization;
  return typeof authHeader === 'string' && authHeader.trim().toLowerCase().startsWith('bearer ');
}

// Request interceptor: ensure csrf cookie for mutating requests.
api.interceptors.request.use(
  async (config) => {
    const storedToken = sessionStorage.getItem('auth_token');
    if (storedToken && !isPublicApiRequest(config.url || '', config.method || 'get')) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
    }

    // Token-authenticated requests do not need Sanctum csrf-cookie bootstrap.
    if (shouldEnsureCsrfCookie(config) && !hasBearerAuthorization(config)) {
      await ensureCsrfCookie();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error?.config?.url || '';
    const requestMethod = error?.config?.method || 'get';
    const skipAuthRedirect = error?.config?.skipAuthRedirect === true;
    if (error.response?.status === 401) {
      // Session bootstrap calls can legitimately return 401 on public pages.
      // Only redirect when user is currently inside protected app routes.
      if (!skipAuthRedirect && !isPublicApiRequest(requestUrl, requestMethod)) {
        sessionStorage.removeItem('auth_token');
      }
      if (
        !skipAuthRedirect &&
        !isPublicApiRequest(requestUrl, requestMethod) &&
        !isPublicPagePath(window.location.pathname) &&
        window.location.pathname !== '/login' &&
        isProtectedAppPath(window.location.pathname)
      ) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
